import React from 'react';
import { Copy, QrCode, Eye, Package, Edit, Trash2, Share2, TrendingUp, Users, PlayCircle, Calendar, CheckCircle, Clock } from 'lucide-react';
import { Wheel } from '../../../../types';
import LoadingSpinner from '../../../../components/LoadingSpinner';

interface WheelsListProps {
  wheels: Wheel[];
  wheelType: 'instant' | 'scheduled';
  companySlug?: string;
  isLoadingWheelProducts?: boolean;
  onCopyUrl: (slug: string) => void;
  onGenerateQR: (slug: string) => void;
  onManageProducts: (wheel: Wheel) => void;
  onEdit: (wheel: Wheel) => void;
  onDelete: (wheel: Wheel) => void;
  onDuplicate: (wheel: Wheel) => void;
  onExecuteDraw?: (wheel: Wheel) => void;
}

const WheelsList: React.FC<WheelsListProps> = ({
  wheels,
  wheelType,
  companySlug,
  isLoadingWheelProducts,
  onCopyUrl,
  onGenerateQR,
  onManageProducts,
  onEdit,
  onDelete,
  onDuplicate,
  onExecuteDraw
}) => {
  const formatDrawDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  if (wheels.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-tinta/10 shadow-sm">
        <p className="text-tinta-soft italic">
          {wheelType === 'instant'
            ? 'Nenhuma roda instantânea encontrada'
            : 'Nenhum sorteio agendado encontrado'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
      {wheels.map(wheel => {
        const statusBadge =
          wheel.status === 'active'
            ? 'bg-musgo/15 text-musgo border-musgo/30'
            : wheel.status === 'draft'
              ? 'bg-tinta/10 text-tinta-soft border-tinta/20'
              : 'bg-mostarda/20 text-mostarda-dark border-mostarda/40';
        const statusLabel =
          wheel.status === 'active' ? 'Ativa' : wheel.status === 'draft' ? 'Rascunho' : 'Pausada';

        return (
          <div
            key={wheel.id}
            className="bg-white rounded-xl border border-tinta/10 hover:shadow-md hover:border-tinta/20 transition-all p-4"
          >
            {/* Header */}
            <div className="mb-4 pb-3 border-b border-tinta/10">
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-tinta truncate leading-tight">{wheel.name}</h3>
                </div>
                <span
                  className={`flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border ${statusBadge}`}
                >
                  {statusLabel}
                </span>
              </div>
              <p className="text-xs text-tinta-soft/70 italic">
                Criado em {wheel.created_at ? new Date(wheel.created_at).toLocaleDateString('pt-BR') : '-'}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4 pb-3 border-b border-tinta/10">
              {wheelType === 'instant' ? (
                <>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-bordo" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Giros</p>
                      <p className="font-display text-base font-bold text-tinta">{wheel.total_spins || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Share2 className="h-4 w-4 text-musgo" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Compartilhamentos</p>
                      <p className="font-display text-base font-bold text-tinta">{wheel.shares_count || 0}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-bordo" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Participantes</p>
                      <p className="font-display text-base font-bold text-tinta">{wheel.total_spins || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {wheel.draw_executed ? (
                      <CheckCircle className="h-4 w-4 text-musgo" />
                    ) : (
                      <Clock className="h-4 w-4 text-mostarda-dark" />
                    )}
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Status</p>
                      <p className="text-sm font-bold italic text-tinta">
                        {wheel.draw_executed ? 'Realizado' : 'Pendente'}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Draw Date for Scheduled */}
            {wheelType === 'scheduled' && wheel.draw_date && (
              <div className="mb-4 pb-3 border-b border-tinta/10">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-bordo" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Data do Sorteio</p>
                    <p className="font-display text-sm font-bold text-tinta">
                      {formatDrawDate(wheel.draw_date)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2">
              {/* Primary Actions */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onEdit(wheel)}
                  className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-bordo bg-bordo/8 border border-bordo/20 rounded-lg hover:bg-bordo/15 transition-colors"
                >
                  <Edit size={16} />
                  Editar
                </button>
                <button
                  disabled={isLoadingWheelProducts}
                  onClick={() => onManageProducts(wheel)}
                  className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-mostarda-dark bg-mostarda/20 border border-mostarda/40 rounded-lg hover:bg-mostarda/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingWheelProducts ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Package size={16} />
                  )}
                  Produtos
                </button>
              </div>

              {/* Active Wheel Actions */}
              {wheel.status === 'active' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => onCopyUrl(wheel.slug)}
                      className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-semibold text-tinta-soft bg-white border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta transition-colors"
                    >
                      <Copy size={16} />
                      Copiar Link
                    </button>
                    <button
                      onClick={() => onGenerateQR(wheel.slug)}
                      className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-semibold text-tinta-soft bg-white border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta transition-colors"
                    >
                      <QrCode size={16} />
                      Código QR
                    </button>
                  </div>
                  <button
                    onClick={() => window.open(`http://participar.rodadepremios.com.br/w/${companySlug}/${wheel.slug}`, '_blank')}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-musgo bg-musgo/10 border border-musgo/30 rounded-lg hover:bg-musgo/15 transition-colors"
                  >
                    <Eye size={16} />
                    {wheelType === 'instant' ? 'Visualizar Sorteio' : 'Visualizar Formulário'}
                  </button>
                </>
              )}

              {/* Execute Draw button for scheduled wheels */}
              {wheelType === 'scheduled' && !wheel.draw_executed && wheel.status === 'active' && onExecuteDraw && (
                <button
                  onClick={() => onExecuteDraw(wheel)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-creme bg-bordo rounded-lg hover:bg-bordo-dark transition-colors"
                >
                  <PlayCircle size={16} />
                  Executar Sorteio
                </button>
              )}

              {/* Duplicate */}
              <button
                onClick={() => onDuplicate(wheel)}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-semibold text-tinta-soft bg-white border border-tinta/15 rounded-lg hover:bg-creme-light hover:text-tinta transition-colors"
              >
                <Copy size={16} />
                Duplicar
              </button>
              {/* Delete */}
              <button
                onClick={() => onDelete(wheel)}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-semibold text-tinta-soft bg-white border border-tinta/15 rounded-lg hover:bg-bordo/8 hover:text-bordo hover:border-bordo/30 transition-colors"
              >
                <Trash2 size={16} />
                Excluir
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WheelsList;
